import {
    AIDuplicateTrainedModel,
    AIRunModel, BaseDataModel,
    CreateModel, MetricsResult,
    OnnxUploadSession,
    OnnxUploadStart,
    TrainedModel
} from "../Models/Models";
import RespondClient from "../Models/RespondClient";
import { getApiHost } from "../util";
import { getInstance } from "./InstanceProvider";

export default class TrainedModelProvider {
    public dataProvider: TrainedModelProvider | undefined;

    public init() {
        if (!this.dataProvider) {
            this.dataProvider = new TrainedModelProvider();
        }
    }

    public get(id: string): Promise<TrainedModel> {
        return getInstance().get(`${getApiHost()}/api/model/${id}`);
    }

    public getByName(name: string, schema: string): Promise<TrainedModel> {
        return getInstance().get(`${getApiHost()}/api/model/${schema}/${name}`);
    }

    public getAll(id: string): Promise<TrainedModel[]> {
        return getInstance().get(`${getApiHost()}/api/schema/${id}/models`);
    }

    public remove(id: string): Promise<boolean> {
        return getInstance().delete(`${getApiHost()}/api/model/${id}`);
    }

    public create(data: CreateModel): Promise<TrainedModel> {
        return getInstance().post(`${getApiHost()}/api/model`, data);
    }

    public startOnnxUpload(id: string, data: OnnxUploadStart): Promise<RespondClient<OnnxUploadSession>> {
        return getInstance().post(`${getApiHost()}/api/model/${id}/onnx/uploads`, data);
    }

    public uploadOnnxChunk(id: string, uploadId: string, index: number, chunk: Blob): Promise<RespondClient<string>> {
        return getInstance().put(`${getApiHost()}/api/model/${id}/onnx/uploads/${uploadId}/chunks/${index}`, chunk, {
            headers: { "Content-Type": "application/octet-stream" }
        });
    }

    public completeOnnxUpload(id: string, uploadId: string): Promise<RespondClient<string>> {
        return getInstance().post(`${getApiHost()}/api/model/${id}/onnx/uploads/${uploadId}/complete`);
    }

    public abortOnnxUpload(id: string, uploadId: string): Promise<RespondClient<string>> {
        return getInstance().delete(`${getApiHost()}/api/model/${id}/onnx/uploads/${uploadId}`);
    }

    public run(id: string, data: AIRunModel): Promise<TrainedModel> {
        return getInstance().post(`${getApiHost()}/api/model/${id}/run`, data);
    }

    public retrain(data: { id: string, force: boolean }): Promise<RespondClient<TrainedModel>> {
        return getInstance().post(`${getApiHost()}/api/model/${data.id}?force=${data.force}`, data);
    }

    public recalculateMetrics(data: { id: string }): Promise<RespondClient<TrainedModel>> {
        return getInstance().post(`${getApiHost()}/api/model/${data.id}/recalculate-metrics`);
    }

    public cancel(id: string): Promise<RespondClient<string>> {
        return getInstance().delete(`${getApiHost()}/api/model/${id}/cancel`);
    }

    public calculateMetrics( id: string, data: BaseDataModel): Promise<RespondClient<MetricsResult>> {
        return getInstance().post(`${getApiHost()}/api/model/${id}/calculate-metrics`, data);
    }

    public duplicate(data: AIDuplicateTrainedModel): Promise<TrainedModel> {
        return getInstance().post(`${getApiHost()}/api/model/duplicate`, data);
    }

    public top(): Promise<RespondClient<TrainedModel[]>> {
        return getInstance().get(`${getApiHost()}/api/model/top`);
    }
}

export const TrainedModelProviderInstance = new TrainedModelProvider();
